import {
  CreateSpaceDialog,
  SharedSpaceCard,
  SpaceCard,
  useConnectSpaces,
  useSharedConnectSpaces,
} from "@/features/connect";
import { connectSpacesListQueryOptions } from "@/features/connect/hooks/useConnectSpaces";
import { validateWorkspaceSearch } from "@/features/workspace-router";
import {
  Button,
  SegmentedControl,
  Skeleton,
  SurfaceEmptyState,
  SurfaceHeader,
  SurfaceLayout,
} from "@ando/ui";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Inbox, Network, Plus, ShieldCheck } from "lucide-react";
import * as React from "react";

/**
 * Ando Connect hub — owner's directory of cross-company Spaces.
 *
 * This is the INDEX child of the ``connect`` layout (``connect.tsx`` renders
 * the <Outlet/>); the sibling ``connect.$spaceId.tsx`` is the detail view.
 * Splitting hub (index) from detail this way is what lets a Space actually
 * OPEN — see the layout docstring.
 *
 * Two lanes (``SegmentedControl``, same pattern as the Condivisioni hub):
 *   - "I miei spazi"     → owned Spaces (``GET /connect/spaces``).
 *   - "Condivisi con me" → spaces another org shared with the caller
 *     (``GET /connect/spaces/shared`` via ``useSharedConnectSpaces``),
 *     rendered through the SAME glass card (``SharedSpaceCard``).
 *
 * ``?sub=shared`` picks the lane. Arriving without ``sub`` while owning no
 * space but holding at least one shared one lands on the shared lane.
 *
 * Primary "Nuovo spazio" CTA sits TOP-RIGHT in the SurfaceHeader actions
 * slot (same placement as ``work.tsx`` "Nuovo"). Cards stagger in on enter.
 */
export const Route = createFileRoute("/_authed/workspace/connect/")({
  validateSearch: validateWorkspaceSearch,
  // Warm the connect-spaces list on hover/focus so the hub paints from cache.
  loader: ({ context: { queryClient } }) => {
    void queryClient.ensureQueryData(connectSpacesListQueryOptions());
  },
  component: ConnectHub,
});

const SKELETON_KEYS = ["s1", "s2", "s3", "s4", "s5", "s6"] as const;

type Lane = "mine" | "shared";

function laneFromSub(sub: string | undefined): Lane | null {
  return sub === "shared" || sub === "mine" ? sub : null;
}

function ConnectHub() {
  const { i18n } = useLingui();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [createOpen, setCreateOpen] = React.useState(false);
  const [lane, setLane] = React.useState<Lane>(() => laneFromSub(search.sub) ?? "mine");
  const { data, isLoading, isError, refetch } = useConnectSpaces();
  const {
    data: sharedData,
    isLoading: sharedLoading,
    isError: sharedError,
    refetch: sharedRefetch,
  } = useSharedConnectSpaces();

  const spaces = data ?? [];
  const total = spaces.length;
  const sharedSpaces = sharedData ?? [];
  const sharedCount = sharedSpaces.length;

  // No lane in the URL, nothing of mine but at least one shared → land there, once.
  const autoLaned = React.useRef(laneFromSub(search.sub) !== null);
  React.useEffect(() => {
    if (autoLaned.current || isLoading || sharedLoading) return;
    autoLaned.current = true;
    if (total === 0 && sharedCount > 0) setLane("shared");
  }, [isLoading, sharedLoading, total, sharedCount]);

  const selectLane = (next: Lane) => {
    setLane(next);
    void navigate({ search: (prev) => ({ ...prev, sub: next }), replace: true });
  };

  const activeLoading = lane === "mine" ? isLoading : sharedLoading;

  const meta = activeLoading ? (
    <span className="text-ink-2">
      <Trans>Caricamento…</Trans>
    </span>
  ) : lane === "shared" ? (
    <>
      <strong className="tabular-nums">{sharedCount}</strong>
      {" · "}
      <Trans>Condivisi con me</Trans>
    </>
  ) : (
    <>
      <strong className="tabular-nums">{total}</strong>{" "}
      {total === 1 ? <Trans>spazio</Trans> : <Trans>spazi</Trans>}
      {" · "}
      {total > 0 ? (
        <Trans>
          Condividi una porzione del tuo workspace con ospiti esterni · tu decidi esattamente cosa
          possono interrogare
        </Trans>
      ) : (
        <Trans>
          Crea uno spazio per far interrogare ai tuoi ospiti SOLO le fonti che scegli — in sola
          lettura, senza accedere al resto
        </Trans>
      )}
    </>
  );

  const newSpaceCta = (
    <Button variant="primary" size="md" onClick={() => setCreateOpen(true)}>
      <Plus size={14} strokeWidth={2.2} aria-hidden />
      <Trans>Nuovo spazio</Trans>
    </Button>
  );

  return (
    <SurfaceLayout
      header={
        <SurfaceHeader title={i18n._(msg`Spazi Connect`)} meta={meta} actions={newSpaceCta} />
      }
      toolbar={
        <div className="px-8 pt-4 pb-2">
          <SegmentedControl<Lane>
            options={[
              { value: "mine", label: i18n._(msg`I miei spazi`), badge: total },
              { value: "shared", label: i18n._(msg`Condivisi con me`), badge: sharedCount },
            ]}
            value={lane}
            onChange={selectLane}
            ariaLabel={i18n._(msg`Spazi Connect`)}
          />
        </div>
      }
    >
      {lane === "mine" ? (
        isError ? (
          <SurfaceEmptyState
            icon={<Network size={18} strokeWidth={1.75} />}
            title={i18n._(msg`Impossibile caricare gli spazi`)}
            description={i18n._(msg`Riprova fra qualche secondo.`)}
            action={
              <Button variant="secondary" size="md" onClick={() => void refetch()}>
                <Trans>Riprova</Trans>
              </Button>
            }
          />
        ) : isLoading ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {SKELETON_KEYS.map((k) => (
              <div
                key={k}
                className="flex flex-col gap-3 rounded-[16px] border border-hairline/70 bg-cream p-4"
              >
                <Skeleton shape="text" className="h-4 w-2/3" />
                <Skeleton shape="text" className="h-3 w-full" />
                <div className="flex gap-1.5">
                  <Skeleton shape="pill" className="h-5 w-14" />
                  <Skeleton shape="pill" className="h-5 w-14" />
                </div>
                <Skeleton shape="text" className="h-3 w-1/3" />
              </div>
            ))}
          </div>
        ) : total === 0 ? (
          <SurfaceEmptyState
            icon={<ShieldCheck size={18} strokeWidth={1.75} />}
            title={i18n._(msg`Ancora nessuno spazio`)}
            description={i18n._(
              msg`Crea il tuo primo spazio Connect per condividere documenti, basi di conoscenza o fonti connesse con ospiti esterni — in totale controllo.`,
            )}
            action={
              <Button variant="primary" size="md" onClick={() => setCreateOpen(true)}>
                <Plus size={14} strokeWidth={2.2} aria-hidden />
                <Trans>Nuovo spazio</Trans>
              </Button>
            }
          />
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {spaces.map((space, i) => (
              <SpaceCard
                key={space.id}
                space={space}
                // Entrance stagger 30–50ms between cards.
                stagger={Math.min(i, 8) * 40}
              />
            ))}
          </div>
        )
      ) : sharedError ? (
        <SurfaceEmptyState
          icon={<Network size={18} strokeWidth={1.75} />}
          title={i18n._(msg`Impossibile caricare gli spazi`)}
          description={i18n._(msg`Riprova fra qualche secondo.`)}
          action={
            <Button variant="secondary" size="md" onClick={() => void sharedRefetch()}>
              <Trans>Riprova</Trans>
            </Button>
          }
        />
      ) : sharedLoading ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {SKELETON_KEYS.slice(0, 3).map((k) => (
            <div
              key={k}
              className="flex flex-col gap-3 rounded-[16px] border border-hairline/70 bg-cream p-4"
            >
              <Skeleton shape="text" className="h-4 w-2/3" />
              <Skeleton shape="pill" className="h-5 w-24" />
              <div className="flex items-center justify-between">
                <Skeleton shape="pill" className="h-5 w-20" />
                <Skeleton shape="text" className="h-3 w-12" />
              </div>
            </div>
          ))}
        </div>
      ) : sharedCount === 0 ? (
        <SurfaceEmptyState
          icon={<Inbox size={18} strokeWidth={1.75} />}
          title={i18n._(msg`Nessuna condivisione ricevuta`)}
          description={i18n._(
            msg`Gli spazi a cui ti invitano appariranno qui. Apri l'invito che ricevi via email per accedere.`,
          )}
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {sharedSpaces.map((space, i) => (
            <SharedSpaceCard key={space.id} space={space} stagger={Math.min(i, 8) * 40} />
          ))}
        </div>
      )}

      <CreateSpaceDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(spaceId) =>
          void navigate({
            to: "/workspace/connect/$spaceId",
            params: { spaceId },
          })
        }
      />
    </SurfaceLayout>
  );
}
